import './Theme.css';
import './App.css';
import './Medium.css';
import './Small.css'
import './Mobile.css'
import './SmallMobile.css'
import {Navigate, Route, Routes, useLocation} from "react-router-dom";
import {useEffect} from "react";
import {captureSignupAttribution} from "./service/Attribution";
import Home from "./components/Home";
import Upload from "./components/Upload";
import ErrorBoundary from "./components/ErrorBoundary";
import Error from "./components/Error";
import Submissions from "./components/Submissions";
import Submission from "./components/Submission";
import Settings from "./components/Settings";
import Login from "./components/Login";
import Register from "./components/Register";
import VerifyReport from "./components/VerifyReport";
import ChangePassword from "./components/ChangePassword";
import SimilarWorks from "./components/SimilarWorks";
import SampleSubmission from "./components/SampleSubmission";
import SampleSimilarWorks from "./components/SampleSimilarWorks";
import NetworkGraph from "./components/NetworkGraph";
import DeepThinkers from "./components/DeepThinkers";
import SampleNetworkGraph from "./components/SampleNetworkGraph";
import Admin from "./components/Admin";
import Analytics from "./components/Analytics";
import UserAnalytics from "./components/UserAnalytics";
import UserActivityPage from "./components/UserActivityPage";
import AdminUser from "./components/AdminUser";
import AdminSubmission from "./components/AdminSubmission";
import AdminOutreach from "./components/AdminOutreach";
import AdminGuide from "./components/AdminGuide";
import Onboard from "./components/Onboard";
import ForgotPassword from "./components/ForgotPassword";
import ResetPassword from "./components/ResetPassword";
import Landing from "./components/Landing";
import Faq from "./components/Faq";
import AboutUs from "./components/AboutUs";
import PeerReviews from "./components/PeerReviews";
import ReviewerDashboard from "./components/ReviewerDashboard";
import ReviewForm from "./components/ReviewForm";
import ShowcaseSubmission from "./components/ShowcaseSubmission";
import ShowcaseSimilarWorks from "./components/ShowcaseSimilarWorks";
import ShowcaseNetworkGraph from "./components/ShowcaseNetworkGraph";
import ShowcaseDeepThinkers from "./components/ShowcaseDeepThinkers";
import FeedbackForm from "./components/FeedbackForm";
import Profile from "./components/Profile";
import PublicSubmission from "./components/PublicSubmission";
import Privacy from "./components/Privacy";
import Terms from "./components/Terms";
import Tutorial from "./components/Tutorial";
import FunderLanding from "./components/FunderLanding";
import AdminTraffic from "./components/AdminTraffic";
import FunderCallOverview from "./components/funder/FunderCallOverview";
import FunderSubmissions from "./components/funder/FunderSubmissions";
import FunderProposal from "./components/funder/FunderProposal";
import FunderReviewerManagement from "./components/funder/FunderReviewerManagement";
import FunderCommittee from "./components/funder/FunderCommittee";
import FunderDecisions from "./components/funder/FunderDecisions";
import FunderPortfolio from "./components/funder/FunderPortfolio";
import FunderComparison from "./components/funder/FunderComparison";
import FunderPreprints from "./components/funder/FunderPreprints";
import FunderWhiteSpace from "./components/funder/FunderWhiteSpace";
import { FeatureFlagsProvider } from "./components/FeatureFlagsContext";
import { ThemeProvider } from "./components/ThemeContext";
import FeatureGate from "./components/FeatureGate";
import { ftAccessToken, FEATURES } from "./constants/Constants";
import { trackPageView } from "./service/HttpService";

function RootPage() {
    const loggedIn = !!localStorage.getItem(ftAccessToken);
    return loggedIn ? <Home /> : <Landing />;
}

// A funder-portal route is gated by BOTH the master "funderPortal" flag (kills
// the whole portal) and the page's own flag (so admins can disable one page).
function FunderGate({ feature, children }) {
    return (
        <FeatureGate feature={FEATURES.FUNDER_PORTAL}>
            <FeatureGate feature={feature}>{children}</FeatureGate>
        </FeatureGate>
    );
}

function usePageViews() {
    const location = useLocation();

    // In-app page-view tracking (feeds the admin User Activity page). Fires
    // for logged-out visitors too — the funder portal is public/unlisted, so
    // its audience is mostly anonymous. The backend normalizes the path
    // against a whitelist, so untracked routes (admin, auth, samples) are
    // dropped server-side.
    useEffect(() => {
        trackPageView(location.pathname);
    }, [location.pathname]);

    // Remember ad/referral tags from whichever page a visitor lands on, so a
    // later sign-up can be credited to them (admin Traffic page).
    useEffect(() => {
        captureSignupAttribution(location.search, location.pathname);
    }, [location.search, location.pathname]);

    useEffect(() => {
        if (typeof window.gtag !== "function") return;
        if (process.env.NODE_ENV !== "production") return;  // skip local dev (npm start)
        const host = window.location.hostname;
        if (host === "localhost" || host === "127.0.0.1") return;  // skip local prod builds
        window.gtag("event", "page_view", {
            page_path: location.pathname + location.search,
            page_location: window.location.href,
            page_title: document.title,
        });
    }, [location.pathname, location.search]);
}

function App() {
    usePageViews();
    return (
        <ThemeProvider>
        <ErrorBoundary>
            <FeatureFlagsProvider>
            <Routes>
                <Route path={"/error/*"} element={<Error/>}></Route>
                <Route path={"/"} element={<RootPage/>}/>
                <Route path={"/upload/*"} element={<FeatureGate feature={FEATURES.UPLOAD}><Upload/></FeatureGate>}/>
                <Route path={"/submissions/*"} element={<FeatureGate feature={FEATURES.SUBMISSIONS}><Submissions/></FeatureGate>}/>
                <Route path="/peer-reviews" element={<FeatureGate feature={FEATURES.PEER_REVIEW}><PeerReviews/></FeatureGate>} />
                <Route path="/reviewer" element={<FeatureGate feature={FEATURES.PEER_REVIEW}><ReviewerDashboard/></FeatureGate>} />
                <Route path="/reviewer/review/:id" element={<FeatureGate feature={FEATURES.PEER_REVIEW}><ReviewForm/></FeatureGate>} />
                <Route path="/settings/*" element={<FeatureGate feature={FEATURES.SETTINGS}><Settings/></FeatureGate>} />
                <Route path="/analytics" element={<FeatureGate feature={FEATURES.ANALYTICS}><UserAnalytics/></FeatureGate>} />
                <Route path="/submission" element={<FeatureGate feature={FEATURES.SUBMISSIONS}><Submission/></FeatureGate>} />
                <Route path="/profile" element={<FeatureGate feature={FEATURES.PROFILES} variant="public"><Profile/></FeatureGate>} />
                <Route path="/profile/submission" element={<FeatureGate feature={FEATURES.PROFILES} variant="public"><PublicSubmission/></FeatureGate>} />
                <Route path="/similar-works" element={<FeatureGate feature={FEATURES.SIMILAR}><SimilarWorks/></FeatureGate>} />
                <Route path="/showcase/:key" element={<FeatureGate feature={FEATURES.SHOWCASE} variant="public"><ShowcaseSubmission/></FeatureGate>} />
                <Route path="/showcase/:key/similar" element={<FeatureGate feature={FEATURES.SHOWCASE} variant="public"><ShowcaseSimilarWorks/></FeatureGate>} />
                <Route path="/showcase/:key/network" element={<FeatureGate feature={FEATURES.SHOWCASE} variant="public"><ShowcaseNetworkGraph/></FeatureGate>} />
                <Route path="/showcase/:key/deepthinkers" element={<FeatureGate feature={FEATURES.SHOWCASE} variant="public"><ShowcaseDeepThinkers/></FeatureGate>} />
                <Route path="/sample/submission" element={<SampleSubmission/>} />
                <Route path="/sample/similar-works" element={<SampleSimilarWorks/>} />
                <Route path="/sample/network" element={<SampleNetworkGraph/>} />
                <Route path={"/network/*"} element={<FeatureGate feature={FEATURES.NETWORK}><NetworkGraph/></FeatureGate>}/>
                <Route path="/deepthinkers" element={<FeatureGate feature={FEATURES.DEEP_THINKERS}><DeepThinkers/></FeatureGate>} />
                <Route path="/login" element={<Login/>}/>
                <Route path="/report/verify" element={<VerifyReport/>}/>
                <Route path="/change-password" element={<ChangePassword/>}/>
                <Route path="/forgot-password" element={<ForgotPassword/>}/>
                <Route path="/reset-password" element={<ResetPassword/>}/>
                <Route path="/register" element={<Register/>}/>
                {/* The four "Manage" sections share one component; the route picks
                    which one renders. /admin stays valid as the Users alias. */}
                <Route path="/admin" element={<Admin section="users"/>}/>
                <Route path="/admin/users" element={<Admin section="users"/>}/>
                <Route path="/admin/sections" element={<Admin section="sections"/>}/>
                <Route path="/admin/reviewers" element={<Admin section="reviewers"/>}/>
                <Route path="/admin/showcase" element={<Admin section="showcase"/>}/>
                <Route path="/admin/analytics" element={<Analytics/>}/>
                <Route path="/admin/activity" element={<UserActivityPage/>}/>
                <Route path="/admin/traffic" element={<AdminTraffic/>}/>
                <Route path="/admin/user" element={<AdminUser/>}/>
                <Route path="/admin/submission" element={<AdminSubmission/>}/>
                <Route path="/admin/outreach" element={<AdminOutreach/>}/>
                <Route path="/admin/guide" element={<AdminGuide/>}/>
                <Route path="/funder" element={<FunderGate feature={FEATURES.FUNDER_CALL_OVERVIEW}><FunderCallOverview/></FunderGate>}/>
                <Route path="/funder/submissions" element={<FunderGate feature={FEATURES.FUNDER_SUBMISSIONS}><FunderSubmissions/></FunderGate>}/>
                <Route path="/funder/proposal" element={<FunderGate feature={FEATURES.FUNDER_SUBMISSIONS}><FunderProposal/></FunderGate>}/>
                <Route path="/funder/reviewer-management" element={<FunderGate feature={FEATURES.FUNDER_REVIEWER_MANAGEMENT}><FunderReviewerManagement/></FunderGate>}/>
                <Route path="/funder/committee" element={<FunderGate feature={FEATURES.FUNDER_COMMITTEE}><FunderCommittee/></FunderGate>}/>
                <Route path="/funder/decisions" element={<FunderGate feature={FEATURES.FUNDER_DECISIONS}><FunderDecisions/></FunderGate>}/>
                <Route path="/funder/portfolio" element={<FunderGate feature={FEATURES.FUNDER_PORTFOLIO}><FunderPortfolio/></FunderGate>}/>
                <Route path="/funder/comparison" element={<FunderGate feature={FEATURES.FUNDER_COMPARISON}><FunderComparison/></FunderGate>}/>
                <Route path="/funder/preprints" element={<FunderGate feature={FEATURES.FUNDER_PREPRINTS}><FunderPreprints/></FunderGate>}/>
                <Route path="/funder/white-space" element={<FunderGate feature={FEATURES.FUNDER_WHITE_SPACE}><FunderWhiteSpace/></FunderGate>}/>
                <Route path="/funder/reviewer-dashboard" element={<Navigate to="/funder/committee"/>}/>
                <Route path="/onboard" element={<Onboard/>}/>
                <Route path="/landing" element={<Navigate to="/"/>}/>
                <Route path="/feedback" element={<FeedbackForm/>}/>
                <Route path="/faq" element={<Faq/>}/>
                {/* Ad landing page — deliberately ungated so paid traffic can
                    never hit an Under Construction screen */}
                <Route path="/tutorial" element={<Tutorial/>}/>
                {/* Funder-facing overview of the portal — static copy, no
                    data. Ungated like /tutorial so a link shared with a
                    foundation never lands on Under Construction; the portal
                    routes it points at stay behind FunderGate. */}
                <Route path="/funders" element={<FunderLanding/>}/>
                <Route path="/about" element={<AboutUs/>}/>
                <Route path="/privacy" element={<Privacy/>}/>
                <Route path="/terms" element={<Terms/>}/>
                {/*<Route path={"/upload-batch/*"} element={<UploadBatch/>}/>*/}
                {/*<Route path={"/submissions/*"} element={<Submissions/>}/>*/}
                {/*<Route path={"/submission/*"} element={<Submission/>}/>*/}
                {/*<Route path={"/export-center/*"} element={<ExportCenter/>}/>*/}
                {/*<Route path={"/score-history/*"} element={<ScoreHistory/>}/>*/}
                {/*<Route path={"/network/*"} element={<NetworkGraph/>}/>*/}
                {/*<Route path={"/similar-works/*"} element={<SimilarWorks/>}/>*/}
                {/*<Route path={"/suggested-editorial-action/*"} element={<SuggestedEditorialAction/>}/>*/}
                {/*<Route path={"/impact-vs-field/*"} element={<ImpactVsField/>}/>*/}
                {/*<Route path={"/faq/*"} element={<Faq/>}/>*/}
                {/*<Route path={"/account/*"} element={<UserAccount/>}/>*/}
                {/*<Route path={"/login/*"} element={<LogIn/>}></Route>*/}
                {/*<Route path={"/signup/*"} element={<SignUp/>}></Route>*/}
                {/*<Route path={"/forgot-password/*"} element={<ForgotPassword/>}></Route>*/}
                {/*<Route path={"/reset-password/*"} element={<ResetPassword/>}></Route>*/}
                {/*<Route path={"/feedback/*"} element={<Feedback/>}/>*/}
                {/*<Route path={"/dashboard-settings/*"} element={<DashboardSettings/>}/>*/}
                {/*<Route path={"/about/*"} element={<AboutUs/>}/>*/}
                {/*<Route path={"/onboard/*"} element={<Onboard/>}/>*/}
                {/*<Route path={"/sample/submission/*"} element={<SampleSubmission/>}/>*/}
                {/*<Route path={"/sample/similar-works/*"} element={<SampleSimilarWorks/>}/>*/}
                {/*<Route path={"/sample/network/*"} element={<SampleNetwork/>}/>*/}
                {/*<Route path={"/sample/export-center/*"} element={<SampleExportCenter/>}/>*/}
                {/*<Route path={"/report/verify/*"} element={<VerifyReport/>}/>*/}
                {/*<Route path={"/peer-reviews/*"} element={<SubmissionPeerReviews/>}/>*/}
                {/*<Route path={"/reviews/*"} element={<PeerReviews/>}/>*/}
                <Route path={'*'} element={<Navigate to="/"/>}/>
                {/*<Route path={"/admin/*"} element={<Admin/>}/>\*/}
                {/*<Route path={"/admin/user/*"} element={<AdminUser/>}/>\*/}
            </Routes>
            </FeatureFlagsProvider>
        </ErrorBoundary>
        </ThemeProvider>
    );
}

export default App;
